import { createAdminApi, type AdminApi } from './api/admin';
import { createAuthApi, type AuthApi } from './api/auth';
import { createFleetApi, type FleetApi } from './api/fleet';
import { createSitesApi, type SitesApi } from './api/sites';
import { PortalClient, type PortalClientOptions } from './http-client';

/**
 * `@kraaapf/portal-client` — der typisierte Zugriff auf den Master.
 *
 * ## Die Naht
 *
 * Dieses Paket ist der **einzige** Weg vom Browser zum Master; in den
 * Anwendungen ist `fetch` per ESLint verboten. Dieselbe Bauform wie
 * `node-gateway` im Master, und aus demselben Grund: Eine Änderung am
 * Protokoll fasst genau eine Stelle an.
 *
 * ## Was hier passiert, damit es die Anwendung nicht tun muss
 *
 * - Das Sitzungs-Cookie geht mit.
 * - Jede Antwort wird gegen das Zod-Schema aus `@kraaapf/contracts` geprüft.
 *   Ein umbenanntes Feld fällt hier auf und nicht als leere Tabellenzelle.
 * - Aus `ErrorResponse` wird ein `PortalClientError` mit stabilem Code, der
 *   Meldung des Masters **und** einer Handlungsanweisung.
 * - Bei 401 wird genau einmal aufgefrischt, und zwar gebündelt: Fünf
 *   gleichzeitige Abfragen lösen eine Auffrischung aus, nicht fünf — sonst
 *   erkennt der Master eine Wiederverwendung und zieht die Sitzungskette
 *   zurück.
 */
export { PortalClient, type PortalClientOptions, type RequestOptions } from './http-client';
export { PortalClientError } from './errors';
export { openEventStream, type EventStream, type EventStreamHandlers } from './events';
export type { AdminApi } from './api/admin';
export type { AuthApi } from './api/auth';
export type { FleetApi, NodeEvent } from './api/fleet';
export type { Blueprint, SitesApi } from './api/sites';

/** Alle Bereiche zusammen — das, was eine Anwendung tatsächlich benutzt. */
export interface PortalApi {
  readonly client: PortalClient;
  readonly auth: AuthApi;
  readonly fleet: FleetApi;
  readonly sites: SitesApi;
  readonly admin: AdminApi;
}

export function createPortalApi(options: PortalClientOptions): PortalApi {
  const client = new PortalClient(options);

  return {
    client,
    auth: createAuthApi(client),
    fleet: createFleetApi(client),
    sites: createSitesApi(client),
    admin: createAdminApi(client),
  };
}
